import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ImageOff, Loader2, Plus, Star, X } from 'lucide-react';
import { cn, useToast, FurnitureIcon } from '@easymoving/ui';
import type { Item } from '@easymoving/types';
import { MAX_IMAGES_PER_ITEM } from '@easymoving/types';
import { useBackend } from '../../lib/backend/context';
import { useWorkspace } from '../../context/WorkspaceContext';
import { itemVisual } from '../../lib/items/visual';
import { ImageLimitError, remainingItemImageSlots } from '../../lib/data/imageLimits';

/**
 * The item's photos on the detail page. A big primary image plus a thumbnail
 * strip: add more, tap a thumb to make it primary, or remove one. Falls back to
 * the furniture icon / placeholder when there are no photos.
 */
export function ItemImages({ item }: { item: Item }) {
  const { t } = useTranslation();
  const backend = useBackend();
  const { canEdit, isOwn } = useWorkspace();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const images = item.images ?? [];
  const visibleImages = images.filter((image) => image.url || image.thumbUrl);
  const canManagePhotos = canEdit && isOwn;
  const visual = itemVisual(item);

  async function onPick(files: FileList | null) {
    if (!files || files.length === 0 || busy) return;
    const slots = remainingItemImageSlots(images.length);
    if (slots === 0) {
      toast.push({ title: t('item.photoLimit', { max: MAX_IMAGES_PER_ITEM }), tone: 'danger' });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setBusy(true);
    try {
      // Only take as many as fit under the per-item cap.
      for (const file of Array.from(files).slice(0, slots)) await backend.addImage(item.id, file);
      toast.push({ title: t('item.photoUpdated'), tone: 'success' });
    } catch (err) {
      const atLimit = err instanceof ImageLimitError;
      toast.push({
        title: atLimit ? t('item.photoLimit', { max: MAX_IMAGES_PER_ITEM }) : t('item.photoFailed'),
        tone: 'danger',
      });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="sr-only"
        aria-label={t('item.photo')}
        onChange={(e) => void onPick(e.target.files)}
      />

      {/* Primary image */}
      <div className="relative aspect-[4/3] bg-surface-tint">
        {visual.kind === 'photo' ? (
          <img src={visual.url} alt={item.name} className="w-full h-full object-cover" />
        ) : visual.kind === 'icon' ? (
          <div className="w-full h-full flex items-center justify-center bg-petrol-tint">
            <FurnitureIcon type={visual.iconType} className="w-1/3 h-1/3" />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <ImageOff className="size-10" />
          </div>
        )}
        {canManagePhotos && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="absolute bottom-2 end-2 inline-flex items-center gap-1.5 bg-petrol text-white text-[13px] font-medium rounded-md px-3 py-1.5 shadow-card hover:bg-petrol-deep"
          >
            <Camera className="size-4" />
            {t('item.addPhoto')}
          </button>
        )}
        {busy && (
          <div className="absolute inset-0 bg-bg/50 flex items-center justify-center">
            <Loader2 className="size-7 animate-spin text-petrol" />
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {visibleImages.length > 0 && (
        <div className="flex gap-2 overflow-x-auto p-2 surface-tint" data-testid="image-strip">
          {visibleImages.map((img) => (
            <div key={img.storagePath} className="relative shrink-0">
              <button
                type="button"
                onClick={() => canManagePhotos && void backend.setPrimaryImage(item.id, img.storagePath)}
                aria-label={t('item.makePrimary')}
                className={cn(
                  'block size-14 rounded-md overflow-hidden border-2',
                  img.isPrimary ? 'border-petrol' : 'border-transparent',
                )}
              >
                <img src={img.thumbUrl ?? img.url!} alt="" className="w-full h-full object-cover" />
              </button>
              {img.isPrimary && (
                <span className="absolute top-0.5 start-0.5 bg-petrol text-white rounded-full p-0.5">
                  <Star className="size-3 fill-white" />
                </span>
              )}
              {canManagePhotos && (
                <button
                  type="button"
                  onClick={() => void backend.removeImage(item.id, img.storagePath)}
                  aria-label={t('item.removePhoto')}
                  className="absolute -top-1.5 -end-1.5 bg-ink/80 text-white rounded-full p-0.5 hover:bg-ink"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          ))}
          {canManagePhotos && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              aria-label={t('item.addPhoto')}
              className="shrink-0 size-14 rounded-md border-2 border-dashed border-petrol/40 text-petrol flex items-center justify-center hover:border-petrol hover:bg-petrol-tint/50"
            >
              <Plus className="size-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
